<!-- Search bar (if enabled) -->
@if (config().searchConfig?.enabled) {
  <div class="search-container mb-3">
    <zag-search
      [placeholder]="config().searchConfig?.placeholder || 'Search...'"
      [searchTerm]="searchTerm()"
      (searchTermChange)="searchTerm.set($event)"
      [debounceMs]="config().searchConfig?.debounceMs || 300"
      [minChars]="config().searchConfig?.minChars || 1"
    >
    </zag-search>
  </div>
}

<!-- Loading indicator -->
@if (loading()) {
  <div class="loading-indicator">
    <span zagIcon="progress-activity" class="loading-spinner"></span>
  </div>
} @else {
  <!-- Cards grid -->
  @if (paginatedData().length > 0) {
    <ul
      class="cards-grid"
      role="listbox"
      [attr.aria-label]="'cardView.ariaLabel' | translate"
      [attr.aria-activedescendant]="focusedCardIndex() >= 0 ? 'card-option-' + focusedCardIndex() : null"
      tabindex="0"
      (keydown)="onKeyDown($event)"
    >
      @for (item of paginatedData(); track trackByFn($index, item); let i = $index) {
        <li role="option" [id]="'card-option-' + i" [attr.aria-selected]="focusedCardIndex() === i">
          <zag-card [clickable]="config().clickable || false" [class.focused]="focusedCardIndex() === i" (cardClick)="onCardClick(item)">
            <!-- Header fields -->
            <zag-card-title>
              @for (field of getFieldsForPosition('header'); track field.key) {
                @if (isFieldVisible(item, field)) {
                  @switch (field.type) {
                    @case ('avatar') {
                      <zag-avatar></zag-avatar>
                      <span>{{ formatFieldValue(item, field) }}</span>
                    }
                    @case ('text') {
                      <span [class]="getFieldCssClass(item, field)">
                        @if (field.label) {
                          <span class="field-label">{{ field.label | translate }}:</span>
                        }
                        {{ formatFieldValue(item, field) }}
                      </span>
                    }
                    @case ('badge') {
                      <span class="badge" [class]="getFieldCssClass(item, field)">
                        {{ formatFieldValue(item, field) }}
                      </span>
                    }
                  }
                }
              }
            </zag-card-title>

            <!-- Body fields -->
            <zag-card-body>
              @for (field of getFieldsForPosition('body'); track field.key) {
                @if (isFieldVisible(item, field)) {
                  <div class="field" [class]="getFieldCssClass(item, field)">
                    @switch (field.type) {
                      @case ('text') {
                        @if (field.label) {
                          <span class="label">{{ field.label | translate }}:</span>
                        }
                        <span>{{ formatFieldValue(item, field) }}</span>
                      }
                      @case ('date') {
                        @if (field.label) {
                          <span class="label">{{ field.label | translate }}:</span>
                        }
                        <span>{{ formatFieldValue(item, field) }}</span>
                      }
                      @case ('badge') {
                        <span class="badge" [class]="getFieldCssClass(item, field)">
                          {{ formatFieldValue(item, field) }}
                        </span>
                      }
                    }
                  </div>
                }
              }
            </zag-card-body>

            <!-- Footer fields -->
            <zag-card-footer>
              @for (field of getFieldsForPosition('footer'); track field.key) {
                @if (isFieldVisible(item, field)) {
                  @switch (field.type) {
                    @case ('badge') {
                      <span class="badge" [class]="getFieldCssClass(item, field)">
                        {{ formatFieldValue(item, field) }}
                      </span>
                    }
                    @case ('button') {
                      <button type="button" [class]="getFieldCssClass(item, field)" (click)="onButtonClick(item, field, $event)">
                        @if (field.icon && field.iconPosition !== 'after') {
                          <i [class]="field.icon"></i>
                        }
                        {{ field.label | translate }}
                        @if (field.icon && field.iconPosition === 'after') {
                          <i [class]="field.icon"></i>
                        }
                      </button>
                    }
                    @case ('link') {
                      <button type="button" [class]="getFieldCssClass(item, field)" (click)="onButtonClick(item, field, $event)">
                        @if (field.icon && field.iconPosition !== 'after') {
                          <i [class]="field.icon"></i>
                        }
                        {{ field.label | translate }}
                        @if (field.icon && field.iconPosition === 'after') {
                          <i [class]="field.icon"></i>
                        }
                      </button>
                    }
                    @case ('text') {
                      <span [class]="getFieldCssClass(item, field)">
                        {{ formatFieldValue(item, field) }}
                      </span>
                    }
                  }
                }
              }
            </zag-card-footer>
          </zag-card>
        </li>
      }
    </ul>

    <!-- Pagination controls -->
    @if (pageSize() > 0 && totalPages() > 1) {
      <div class="pagination">
        <button
          zagButton="outlined"
          zagType="secondary"
          zagSize="sm"
          type="button"
          [disabled]="!hasPreviousPage()"
          (click)="previousPage()"
        >
          <span zagIcon="chevron-left"></span>
          {{ 'cardView.pagination.previous' | translate }}
        </button>

        <span class="pagination-info">
          {{
            'cardView.pagination.pageInfo'
              | translate
                : {
                    start: currentPage() * pageSize() + 1,
                    end: currentPage() * pageSize() + paginatedData().length,
                    totalItems: filteredData().length,
                  }
          }}
        </span>

        <button zagButton="outlined" zagType="secondary" zagSize="sm" type="button" [disabled]="!hasNextPage()" (click)="nextPage()">
          {{ 'cardView.pagination.next' | translate }}
          <span zagIcon="chevron-right" zagEnd></span>
        </button>
      </div>
    }
  } @else {
    <!-- Empty state -->
    <div class="no-data-message">
      <p>{{ 'cardView.noData' | translate }}</p>
    </div>
  }
}
